import { RowSelectedEvent } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { GridModalService } from '@app/core/components/grid/modals/grid-modal.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { P2SettingService } from '@app/services/p2-setting.service';

@Component({
    selector: 'tt-order-lines-grid',
    templateUrl: './order-lines-grid.component.html',
    styleUrls: ['./order-lines-grid.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class OrderLinesGridComponent implements OnInit, OnChanges {
    @Input()
    public params?: GerOrderHeadParams;

    @Input()
    public rowSelection?: 'single' | 'multiple';

    @Output()
    public gridReady = new EventEmitter<GridOptions<OrderLineItem>>();

    @Output()
    public rowSelected = new EventEmitter<RowSelectedEvent<OrderLineItem>>();

    public grid?: GridOptions<OrderLineItem>;

    public settingCanCancelOrderhead = false;

    public showButtonDeleteOrderlines = false;

    constructor(
        private remember: RememberService,
        private modal: ModalService,
        private gridModal: GridModalService,
        private p2setting: P2SettingService,
        public cdr: ChangeDetectorRef,
    ) {}

    public onRowChanged(event: RowDataChangeEvent<OrderLineItem>) {
        if (['prod_id', 'prod_name'].includes(event.colId) && !!event.row.prod_id && !event.row.prodchoice_keyno && event.saved === true) {
            this.openProductsConfigModalForRow(event.row);
        }
    }

    public onRowSelected(event: RowSelectedEvent<OrderLineItem>) {
        this.rowSelected.emit(event);
    }

    public read() {
        if (!this.grid) {
            const grid = new Grid<OrderLineItem>()
                .setRememberId('w_orderline_dynamic.grid')
                .setLoadDataFunction({ method: 3185, parameters: () => this.params })
                .addToolbarButton({
                    name: 'orderline_cancel',
                    text: 'tt_orderline_grid_cancel',
                    cssClass: 'tt-button tt-button--danger im-grid-btn-xs-r',
                    func: async () => {
                        const submitted = await this.gridModal.openDialogFromDbButton({ component: 'OrderHeadCancelOrderlinesTs' }, { selectedRows: this.grid?.gridfunc?.getIsSelectedRows() ?? [] });

                        if (!!submitted) this.read();
                    },
                    disabled: () => this.canCancelSelectedRows(),
                })
                .addRowButton({
                    name: 'customDelete',
                    icon: 'far-trash-alt',
                    btn_type: 'danger',
                    disabled: () => (!this.showButtonDeleteOrderlines ? 'hidden' : false),
                    func: async (data) => {
                        await this.grid?.gridfunc?.removeRow(data);
                        this.grid?.gridfunc?.saveChanges(false);
                    },
                })
                .addRowButton({
                    name: 'edit_product_config',
                    icon: 'fas fa-cogs',
                    tooltip: 'edit_product_config',
                    translateTooltip: true,
                    func: (row) => this.openProductsConfigModalForRow(row),
                    disabled: (row) => ([0, '0', false, 'false'].includes(row?.show_productsconfig_button ?? '0') ? 'hidden' : false),
                })
                .addRowButton({
                    name: 'w_orderline_calc_form_edit',
                    icon: 'fas fa-calculator',
                    tooltip: 'w_orderline_calc_form_edit',
                    translateTooltip: true,
                    func: (row) => this.openOrderlineCalcFormModal(row),
                    disabled: (row) => ([0, '0', false, 'false'].includes(row?.show_orderlinecalcform_button ?? '0') ? 'hidden' : false),
                });

            if (!!this.rowSelection) {
                grid.setKendoConfig({ selectable: this.rowSelection });
            }

            this.grid = grid;
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private canCancelSelectedRows() {
        if (!this.settingCanCancelOrderhead) {
            return 'hidden';
        } else {
            const selectedRows = this.grid?.gridfunc?.getIsSelectedRows() ?? [];
            return !(selectedRows.length > 0 && selectedRows.filter((row) => row['delivered_quantity'] >= row['ordered_quantity'] || row['invoiced_quantity'] > row['delivered_quantity']).length === 0);
        }
    }

    private async openProductsConfigModalForRow(row: OrderLineItem) {
        await this.remember.remember('productsconfig_selected', btoa(JSON.stringify([{ item_id: row.prod_id, item_name: row.prod_name, quantity: 1, editable: false }])));
        const submitted = await this.modal.openStateDialog({ state: 'productsconfig', params: `orderline/${this.params!.order_internal_no}/${row.orderline_keyno}` });

        if (!!submitted) {
            this.read();
        }
    }

    private async openOrderlineCalcFormModal(row: OrderLineItem) {
        const submitted = await this.modal.openStateDialog({ state: 'orderlinecalcform', params: `${this.params!.order_internal_no}/orderline_keyno/${row.orderline_keyno}` });

        if (!!submitted) {
            this.read();
        }
    }

    public onGridReady() {
        this.gridReady.emit(this.grid);
        this.cdr.markForCheck();
    }

    private async loadSettingCanCancelOrderhead() {
        this.settingCanCancelOrderhead = (await this.p2setting.getP2SettingValue({ p2_setting_keyno: 420 })) === '1';
    }

    private async loadShowButtonDeleteOrderlines() {
        this.showButtonDeleteOrderlines = (await this.p2setting.getP2SettingValue({ p2_setting_keyno: 158 })) === '1';
    }

    ngOnInit(): void {
        this.loadSettingCanCancelOrderhead();
        this.loadShowButtonDeleteOrderlines();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.read();
        }
    }

    // vm.dynamicGrid = new ttGridFactory({
    //                   dataTask: {
    //                       rememberId: 'w_orderline_dynamic.grid',
    //                       loadData: {
    //                           method: 3185,
    //                           parameters: {
    //                               order_internal_no: $stateParams.order_internal_no,
    //                           },
    //                       },
    //                       addRow: {
    //                           //
    //                           method: null,
    //                           //parameters: null,
    //                           parameters: {
    //                               order_internal_no: $stateParams.order_internal_no,
    //                           },
    //                       },
    //                       removeRow: {
    //                           //
    //                           method: null,
    //                           parameters: null,
    //                       },
    //                       saveData: {
    //                           method: null,
    //                           parameters: {
    //                               order_internal_no: $stateParams.order_internal_no,
    //                           },
    //                       },
    //                   },
    //               })
    //                   .setConfig({ shortcuts: true })
    //                   .setNavigation({ altNav: true, newLine: true })
    //                   .setKendoConfig({ aggregate: [], height: '100%' })
    //                   .setSpecialFunc({
    //                       buttons: [
    //                           {
    //                               name: 'customDelete',
    //                               text: '',
    //                               icon: 'far-trash-alt',
    //                               type: 'danger',
    //                               disabled: () => (vm.model.orderhead.show_button_deleteorderlines !== '1' ? 'hidden' : false),
    //                               func: async function (data) {
    //                                   //modalService.show({
    //                                   //    type: 'warning',
    //                                   //    title: vm.translations.tt_delete_grid_row_title,
    //                                   //    message: vm.translations.tt_delete_grid_row_warning,
    //                                   //    buttons: [{
    //                                   //        label: vm.translations.tt_delete_grid_row_abort,
    //                                   //        cssClass: 'tt-button--warning',
    //                                   //        action: function (dialogItself) {
    //                                   //            dialogItself.close();
    //                                   //        }
    //                                   //    }, {
    //                                   //        label: vm.translations.tt_delete_grid_row_confirm,
    //                                   //        cssClass: 'tt-button--danger',
    //                                   //        action: async function (dialogItself) {
    //                                   await vm.dynamicGrid.gridfunc.removeRow(data);
    //                                   vm.dynamicGrid.gridfunc.saveChanges(false);
    //                                   //dialogItself.close();
    //                                   //    }
    //                                   //}]
    //                                   //});
    //                               },
    //                           },
    //                           buildEditProductConfigRowButton(),
    //                           buildEditOrderlineCalcFormRowButton(),
    //                       ],
    //                   })
    //                   .setToolbar({
    //                       buttons: [
    //                           {
    //                               name: 'orderline_cancel',
    //                               text: 'tt_orderline_grid_cancel',
    //                               func: function (data) {
    //                                   vm.cancelSelectedLines();
    //                               }, // vm.dynamicGrid.gridfunc.getIsSelectedRows
    //                               icon: 'none',
    //                               cssClass: 'tt-button tt-button--danger im-grid-btn-xs-r',
    //                               translate: true,
    //                               disabled: function () {
    //                                   return vm.model.orderhead.setting_enable_oh_cancel !== '1' ? 'hidden' : vm.model.lockedCancel ? true : vm.canCancelRows() ? false : true;
    //                               },
    //                           },
    //                       ],
    //                   });

    //               function buildEditProductConfigRowButton() {
    //                   return {
    //                       name: 'edit_product_config',
    //                       icon: 'fas fa-cogs',
    //                       tooltip: 'edit_product_config',
    //                       translateTooltip: true,
    //                       func: (row) => openProductsConfigModalForRow(row),
    //                       disabled: (row, params) => {
    //                           if ([0, '0', false, 'false'].includes(row['show_productsconfig_button'])) {
    //                               return 'hidden';
    //                           }

    //                           return false;
    //                       },
    //                   };
    //               }

    //               function buildEditOrderlineCalcFormRowButton() {
    //                   return {
    //                       name: 'w_orderline_calc_form_edit',
    //                       icon: 'fas fa-calculator',
    //                       tooltip: 'w_orderline_calc_form_edit',
    //                       translateTooltip: true,
    //                       func: (row) => openOrderlineCalcFormModal(row),
    //                       disabled: (row, params) => {
    //                           if ([0, '0', false, 'false'].includes(row['show_orderlinecalcform_button'] ?? '0')) {
    //                               return 'hidden';
    //                           }

    //                           return false;
    //                       },
    //                   };
    //               }

    //               async function openOrderlineCalcFormModal(row) {
    //                   const submitted = await tsModalService.openStateDialog({ state: 'orderlinecalcform', params: `${vm.model.orderhead.order_internal_no}/orderline_keyno/${row['orderline_keyno']}` });

    //                   if (!!submitted) {
    //                       vm.dynamicGrid?.gridfunc?.read();
    //                   }
    //               }

    //               vm.onRowChanged = function (event) {
    //                   if (['prod_id', 'prod_name'].includes(event.colId) && !!event.row['prod_id'] && !event.row['prodchoice_keyno'] && event.saved === true) {
    //                       openProductsConfigModalForRow(event.row);
    //                   }
    //               };

    //               async function openProductsConfigModalForRow(row) {
    //                   await rememberService.remember('productsconfig_selected', btoa(JSON.stringify([{ item_id: row['prod_id'], item_name: row['prod_name'], quantity: 1, editable: false }])));
    //                   const submitted = await tsModalService.openStateDialog({ state: 'productsconfig', params: `orderline/${vm.model.orderhead.order_internal_no}/${row['orderline_keyno']}` });

    //                   if (!!submitted) {
    //                       vm.dynamicGrid?.gridfunc?.read();
    //                   }
    //               }
}

interface GerOrderHeadParams {
    order_internal_no: string;
}

export interface OrderLineItem {
    orderline_keyno: number;
    item_glyphicon: string;
    item_glyphicon_color: string;
    is_selected: boolean;
    prod_id: string;
    prod_name: string;
    prodsize_keyno: number;
    prodchoice_keyno: number;
    prodchoice_string: string;
    prodgrp_name: string;
    prodgrp2_name: string;
    unit_name: string;
    input_unit_no: number;
    input_unit_name: string;
    price_locval: number;
    price_ordval: number;
    price_net_ordval: number;
    price_net1_ordval: number;
    input_price_ordval: number;
    discount_pst: number;
    orderdiscount_pst: number;
    amount_net_ordval: number;
    amount1_ordval: number;
    amount2_ordval: number;
    amount3_locval: number;
    estimated_cost_locval: number;
    estimated_cost_amount_locval: number;
    db: number;
    dg_pst: number;
    input_quantity: number;
    quantity_priceunit: number;
    ordered_quantity: number;
    delivered_quantity: number;
    invoiced_quantity: number;
    volume: number;
    colli_quantity: number;
    spesial_product: string;
    prod_id_old: string;
    disposable: number;
    available: number;
    quantity_stock: number;
    po_keyno: string;
    stockplace_id: string;
    stockplace_name: string;
    post: string;
    weight: number;
    price_incl: number;
    amount_incl: number;
    note_before: string;
    orderline_note: string;
    confirmed_date: Date;
    confirmed_week: number;
    nobb_no: number;
    edi_orderline_status_keyno: number;
    edi_orderline_status_name: string;
    project_keyno: number;
    project_name: string;
    department_id: string;
    department_name: string;
    labour_no: number;
    labour_name: string;
    direct_purchase_created: boolean;
    item_thumb: string;
    reg_datetime: Date;
    mod_datetime: Date;
    item_state: string;
    item_parms: string;
    item_path: string;
    orderby: number;
    order_internal_no: number;
    product_dim: string;
    product_note: string;
    pick_quantity: number;
    tt_gridrow_id: string;
    show_productsconfig_button: boolean;
    show_orderlinecalcform_button: boolean;
    requisitions: string;
    components: string;
}
